import { RenderContext } from '@/webgl/core/RenderContext'
import Signal from '@/webgl/core/Signal'
import { BASE_SETTINGS } from '@/webgl/runner/entities/SettingsEntity'
import { TimeEntity } from '@/webgl/runner/entities/Time'
import GameWorld from '@/webgl/runner/GameWorld'
import { IRunnerRenderer } from '@/webgl/runner/interfaces/IRunnerRenderer'
import { ISystem } from '@/webgl/runner/interfaces/ISystem'
import { ScenarioSetup } from '@/webgl/runner/scenarios/ScenarioSetup.types'

export interface IGameSegment {
  id: string
  onComplete: Signal<void>
  init(world: GameWorld, renderer: IRunnerRenderer, setup: ScenarioSetup): void
  start(): void
  load?(): Promise<unknown>
  update?(time: TimeEntity): void
  render?(context: RenderContext): void
  stop(): void
}

export default class SequencingSystem implements ISystem {
  segments: IGameSegment[] = []
  current: number = -1
  renderer: IRunnerRenderer
  setup: ScenarioSetup
  startIndex: number = 0
  private disposed: boolean = false

  get active() {
    return this.segments[this.current]
  }

  constructor(readonly world: GameWorld) {}

  init(renderer: IRunnerRenderer, setup: ScenarioSetup): SequencingSystem {
    this.renderer = renderer
    this.setup = setup
    if (setup.speed) {
      this.world.settings.minSpeed = setup.speed.min
      this.world.settings.maxSpeed = setup.speed.max
    } else {
      this.world.settings.minSpeed = BASE_SETTINGS.MIN_SPEED
      this.world.settings.maxSpeed = BASE_SETTINGS.MAX_SPEED
    }
    return this
  }

  setStartSegmentId(id: string) {
    const idx = this.segments.findIndex((s) => s.id === id)
    if (idx >= 0) {
      this.startIndex = idx
    }
  }

  start() {
    this.setActiveSegment(this.startIndex)
  }

  add(segment: IGameSegment): SequencingSystem {
    this.segments.push(segment)
    segment.init(this.world, this.renderer, this.setup)
    return this
  }

  async load(abort: AbortSignal) {
    const promises = this.segments.map((s) => s.load?.())
    return Promise.all(promises)
  }

  setActiveSegment(tgt: number) {
    if (this.disposed) return
    if (tgt == this.current) return

    if (this.active) {
      this.active.onComplete.off(this.segmentComplete)
      this.active.stop()
    }

    if (tgt >= this.segments.length) return

    this.current = tgt
    this.active.onComplete.on(this.segmentComplete)
    this.active.start()

    this.world.state.setCurrentSegment(this.active.id, this.active.constructor.name)
  }

  segmentComplete = () => {
    if (this.disposed) return
    const next = this.current + 1
    if (next >= this.segments.length) {
      this.world.state.actor.send({ type: 'STOP' })
      return
    }
    this.setActiveSegment(next)
  }

  update(time: TimeEntity): void {
    if (this.disposed) return
    this.active?.update?.(time)
  }

  render(context: RenderContext) {
    if (this.disposed) return
    this.active?.render?.(context)
  }

  dispose(): void {
    this.disposed = true
    if (this.active) {
      // Detach the completion listener so a deferred emit (e.g. an audio
      // `onEnded` that fires after teardown) can't advance into a disposed
      // world and access systems that have already been removed.
      this.active.onComplete.off(this.segmentComplete)
    }
    this.active?.stop()
    // for (const segment of this.segments) {
    //   segment.stop();
    // }
  }
}
